import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import {
  catchError,
  finalize,
  Observable,
  shareReplay,
  switchMap,
  throwError
} from "rxjs";
import { AuthService } from "../../services/auth/auth.service";

export const SKIP_TOKEN = new HttpContextToken(() => false);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  // Services
  private readonly authService = inject(AuthService);

  // State
  private refreshToken$: Observable<unknown> | null = null;

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // Add cookies to request
    if (!req.context.get(SKIP_TOKEN)) {
      req = req.clone({ withCredentials: true });
    }

    return next.handle(req).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === HttpStatusCode.Unauthorized) {
            return this.handleRefreshToken(req, next);
          }
        }

        throw error;
      })
    );
  }

  private handleRefreshToken(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (!this.refreshToken$) {
      const onBehalfOfId = this.authService.onBeHalfOfUserId;

      this.refreshToken$ = this.authService
        .login({ onBehalfOf: onBehalfOfId })
        .pipe(
          shareReplay(1),
          catchError((error) => {
            // Rethrow error either way
            return this.authService.logout().pipe(
              switchMap(() => throwError(() => error)),
              catchError(() => throwError(() => error))
            );
          }),
          finalize(() => {
            this.refreshToken$ = null;
          })
        );
    }

    return this.refreshToken$.pipe(
      switchMap(() => {
        return next.handle(req);
      })
    );
  }
}
